import { type QueryClient, QueryClientContext } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useRef } from 'react';

import { fetchWithDefaults } from '@/lib/http/client';

/**
 * Timestamp (ms) of the most recent invalidateCrmQueries call. Shared across
 * the poller's zombie-WS escalation and the WS hook's full-refresh handler
 * so a reconnect triggered by escalation doesn't produce two invalidation
 * waves when the server responds with `full_refresh_required` moments later.
 */
let lastCrmInvalidationAt = 0;

/**
 * True if invalidateCrmQueries ran within the last `windowMs`. Used to
 * coalesce back-to-back invalidation waves (e.g. zombie-WS escalation
 * immediately followed by a reconnect-triggered full refresh).
 */
export function wasCrmInvalidatedRecently(windowMs: number): boolean {
  return Date.now() - lastCrmInvalidationAt < windowMs;
}

/**
 * Test-only: reset the coalescing timestamp. Without this, a prior test that
 * advanced fake timers far into its own mocked "now" can leave
 * lastCrmInvalidationAt ahead of the next test's freshly-reset fake clock,
 * making wasCrmInvalidatedRecently return a false positive.
 */
export function __resetCrmInvalidationCoalesceForTesting(): void {
  lastCrmInvalidationAt = 0;
}

/**
 * Invalidate only CRM-related queries, not everything in the app.
 * Shared by the event poller and WebSocket full-refresh handler.
 */
export function invalidateCrmQueries(queryClient: QueryClient): void {
  lastCrmInvalidationAt = Date.now();
  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey;
      if (!Array.isArray(key)) {
        return false;
      }
      // CRM data queries: ['crm', orgId, ...] — covers detail and list
      // queries alike, including infinite-scroll lists, which now share
      // this namespace via the unified list-key producer
      // (crmQueryKeys.objectsListUnified) instead of a separate,
      // previously-distinct infinite-scroll key family.
      if (key[0] === 'crm') {
        return true;
      }
      // CRM total counts: ['crmTotalCount', orgId, ...]
      if (key[0] === 'crmTotalCount') {
        return true;
      }
      // All CRM aggregate queries (dashboard counts, pipeline metrics, column totals)
      if (key[0] === 'crmAggregate') {
        return true;
      }
      // Timeline view-state queries: ['entity-view-state', entityId, ...]
      // Needed so wake-from-sleep (handleFullRefreshRequired) also refreshes
      // timeline data, not just list/detail queries.
      if (key[0] === 'entity-view-state') {
        return true;
      }
      return false;
    },
  });
}

interface EventVersionResponse {
  latest_event_id: number | null;
}

interface UseCRMEventPollingOptions {
  orgId: number | null;
  enabled?: boolean;
  /**
   * Ref holding the last event ID handled by the WebSocket.
   * Read synchronously on each poll — the WS hook updates this ref
   * in its event handler, so the value is always current without
   * waiting for a React render cycle.
   */
  wsLastEventIdRef?: React.RefObject<number | null>;
  /**
   * WebSocket connection status. When disconnected/connecting, the poller
   * treats the WS as inactive (ignores wsLastEventIdRef) to prevent stale
   * cache during downtime.
   */
  wsStatus?: 'connected' | 'connecting' | 'disconnected';
  /**
   * Forces the WebSocket to reconnect. Invoked by the zombie-WS escalation
   * below when the WS reports 'connected' but stops delivering events.
   */
  forceReconnect?: () => void;
}

const BASE_INTERVAL = 30000; // 30 seconds — used as the primary rate when WS is NOT connected
const WS_HEALTHY_INTERVAL = 60_000; // 60 seconds — silent-drop heartbeat when WS IS connected
const MAX_INTERVAL = 300000; // 5 minutes

/**
 * When the WS is active, allow a small gap before invalidating.
 * A single user action can generate 2-3 events (e.g., property update +
 * side-effect). The WS delivers them nearly instantly, but the poller can
 * fire in between. A gap of ≤ 2 events means the WS will catch up
 * momentarily — no need to refetch.
 *
 * During bulk imports (100+ events), a gap > 2 triggers full query
 * invalidation, ensuring the UI doesn't show stale state (e.g., showing
 * 95/100 contacts imported). The WebSocket catchup mechanism (MAX_CATCHUP_EVENTS=100)
 * ensures eventual consistency, but tighter tolerance improves UX during bulk operations.
 */
const WS_GAP_TOLERANCE = 2;

/**
 * Consecutive stagnant polls (server event id advanced, WS cursor didn't,
 * WS reports 'connected') required before escalating to a forced reconnect.
 * Requiring 2 (not 1) avoids escalating on a single unlucky race between the
 * poll firing and an in-flight WS event being applied.
 */
const ZOMBIE_ESCALATION_THRESHOLD = 2;

/**
 * Window (ms) within which a zombie-WS escalation's own invalidateCrmQueries
 * call suppresses the invalidation normally triggered by a subsequent
 * `full_refresh_required` message (see useCrmWebSocket's
 * handleFullRefreshRequired) — collapses the two into one invalidation wave.
 * Reuses BASE_INTERVAL since that's the poll cadence while stagnant.
 */
const ZOMBIE_INVALIDATION_COALESCE_WINDOW_MS = BASE_INTERVAL;

/**
 * Returns the base polling interval given the current WS status.
 *
 * Connected → 60s (the poll is a silent-drop heartbeat; WS handles real-time).
 * Otherwise → 30s (poller is the primary cache-freshness mechanism).
 */
function getBaseInterval(
  wsStatus: 'connected' | 'connecting' | 'disconnected'
): number {
  return wsStatus === 'connected' ? WS_HEALTHY_INTERVAL : BASE_INTERVAL;
}

/**
 * Adds random jitter (±20%) to prevent synchronized retries across clients.
 */
function withJitter(interval: number): number {
  const jitter = 0.8 + Math.random() * 0.4; // 0.8 to 1.2
  return Math.round(interval * jitter);
}

/**
 * Polls the CRM event version endpoint and invalidates all queries when
 * the event ID changes, keeping cached data fresh across the app.
 *
 * When the WebSocket has already handled an event (wsLastEventIdRef.current >=
 * server's latest_event_id), the poller skips invalidation to avoid
 * triggering refetches that overwrite in-place cache updates made by the
 * WebSocket or optimistic mutations.
 */
export function useCRMEventPolling({
  orgId,
  enabled = true,
  wsLastEventIdRef,
  wsStatus = 'disconnected',
  forceReconnect,
}: UseCRMEventPollingOptions) {
  // Use useContext directly to avoid throwing when no QueryClientProvider exists.
  // This allows the hook to be used in contexts that may not have a QueryClientProvider
  // (e.g., TeamProvider during certain tests). The hook gracefully no-ops in this case.
  const queryClient = useContext(QueryClientContext);
  const lastEventIdRef = useRef<number | null>(null);
  const intervalRef = useRef<number>(BASE_INTERVAL);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef<boolean>(false);
  const activeRef = useRef<boolean>(false);
  const wsStatusRef = useRef(wsStatus);
  // --- Zombie WebSocket detection -----------------------------------------
  // Count of consecutive polls where the server's event id advanced but
  // wsLastEventIdRef did not, while wsStatus stayed 'connected' the whole
  // time. Reset whenever the WS ref advances or status changes (see poll()).
  const stagnantPollCountRef = useRef<number>(0);
  // wsLastEventIdRef's value as of the previous poll, used to detect whether
  // it advanced since then.
  const prevWsEventIdRef = useRef<number | null>(null);
  // wsStatus as of the previous poll, used to detect status changes.
  const prevWsStatusRef = useRef<'connected' | 'connecting' | 'disconnected'>(
    wsStatus
  );

  // Keep wsStatus ref in sync — read via ref in poll() to avoid
  // re-creating the callback on every WS status change.
  wsStatusRef.current = wsStatus;

  const poll = useCallback(async () => {
    // No-op if queryClient is unavailable (no QueryClientProvider)
    if (!queryClient || !orgId || isPollingRef.current) {
      return;
    }

    isPollingRef.current = true;

    try {
      // Use fetchWithDefaults (not apiGet) to access response headers for Retry-After.
      // Pattern from useEntitySearch.ts:190-211.
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/crm-events/event-version/`,
        undefined,
        5000 // 5s timeout — matches useEntitySearch pattern
      );

      // Handle 429 separately: read Retry-After, use non-alarming log level.
      // This returns early and does NOT throw, so it skips the catch block.
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After');
        const retrySeconds = retryAfter ? parseFloat(retryAfter) : null;
        if (retrySeconds && retrySeconds > 0) {
          // Scale up by 1/0.8 so that worst-case jitter (×0.8) still meets the
          // server's Retry-After floor. Without this, withJitter() could reduce a
          // 60s Retry-After to 48s — causing the client to retry before the
          // throttle window expires and triggering a 429 loop.
          intervalRef.current = Math.min(
            Math.ceil((retrySeconds * 1000) / 0.8),
            MAX_INTERVAL
          );
        } else {
          intervalRef.current = Math.min(intervalRef.current * 2, MAX_INTERVAL);
        }
        console.warn('[CRM Polling] Rate limited (429)', {
          orgId,
          retryAfterSeconds: retrySeconds,
          nextInterval: intervalRef.current,
        });
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: EventVersionResponse = await response.json();

      // Read the WS ref synchronously — always has the latest value.
      const wsHandled = wsLastEventIdRef?.current ?? null;

      // Reset the stagnation counter whenever the WS ref advances or its
      // status changes since the previous poll — either signals the WS is
      // (or was) actually doing something, not silently half-open. An idle
      // org never trips this: with no new events, data.latest_event_id
      // never advances, so the "advanced" precondition below never fires.
      const wsAdvancedSincePrevPoll = wsHandled !== prevWsEventIdRef.current;
      const wsStatusChangedSincePrevPoll =
        wsStatusRef.current !== prevWsStatusRef.current;
      if (wsAdvancedSincePrevPoll || wsStatusChangedSincePrevPoll) {
        stagnantPollCountRef.current = 0;
      }
      prevWsEventIdRef.current = wsHandled;
      prevWsStatusRef.current = wsStatusRef.current;

      // Check if the event ID advanced (skip first poll to establish
      // baseline). Strictly greater-than, not just !==: the server's event
      // id is a monotonically increasing counter, so a response reporting a
      // lower id than we've already seen is stale/out-of-order — treat it
      // like "no change" rather than regressing our cursor.
      if (
        lastEventIdRef.current !== null &&
        data.latest_event_id !== null &&
        data.latest_event_id > lastEventIdRef.current
      ) {
        // WS is active only if status is 'connected' AND ref is non-null.
        // When disconnected/connecting, treat WS as inactive to prevent stale cache.
        if (wsHandled !== null && wsStatusRef.current === 'connected') {
          // WS is active. Skip invalidation if the WS has already applied
          // this event OR is only a small gap behind (it'll catch up shortly).
          const gap = data.latest_event_id - wsHandled;

          // The server advanced but the WS cursor didn't move, and status
          // stayed 'connected' throughout — this poll is stagnant.
          if (!wsAdvancedSincePrevPoll && !wsStatusChangedSincePrevPoll) {
            stagnantPollCountRef.current += 1;
          }

          if (stagnantPollCountRef.current >= ZOMBIE_ESCALATION_THRESHOLD) {
            // Half-open WS: reports 'connected' but isn't delivering events.
            // A small gap alone (gap <= WS_GAP_TOLERANCE) would normally be
            // skipped as "WS will catch up shortly" — but stagnation across
            // polls proves it won't. Escalate: refresh the cache now and
            // force the WS to reconnect.
            console.warn('[CRM Polling] Zombie WebSocket detected', {
              orgId,
              latestEventId: data.latest_event_id,
              wsLastEventId: wsHandled,
              stagnantPolls: stagnantPollCountRef.current,
            });
            if (
              !wasCrmInvalidatedRecently(ZOMBIE_INVALIDATION_COALESCE_WINDOW_MS)
            ) {
              invalidateCrmQueries(queryClient);
            }
            forceReconnect?.();
            stagnantPollCountRef.current = 0;
          } else if (gap > WS_GAP_TOLERANCE) {
            // WS is significantly behind — likely missed events.
            // Scope invalidation to CRM-related queries only — a nuclear
            // invalidateQueries() with no args would refetch ALL queries
            // (reasoning, files, user prefs, etc.) — wasteful and disruptive.
            invalidateCrmQueries(queryClient);
          }
          // If gap <= WS_GAP_TOLERANCE and not yet stagnant, skip invalidation
          // (WS will catch up shortly) and continue to update baseline below
        } else {
          // No WS connection (or WS disconnected) — poller is the primary mechanism.
          // Scope to CRM queries only (same rationale as above).
          invalidateCrmQueries(queryClient);
        }
      }

      // Advance the cursor, but never regress it — see the monotonic-id
      // comment above the "advanced" check.
      if (
        data.latest_event_id !== null &&
        (lastEventIdRef.current === null ||
          data.latest_event_id > lastEventIdRef.current)
      ) {
        lastEventIdRef.current = data.latest_event_id;
      }

      // Reset interval on success. The base rate adapts to WS status: 60s
      // when connected (heartbeat only — WS is primary), 30s otherwise
      // (poller is the primary cache-freshness signal). While stagnation is
      // suspected (stagnantPollCountRef > 0), stay at the faster 30s rate
      // regardless of status — halves the time to detect and escalate a
      // zombie connection.
      intervalRef.current =
        stagnantPollCountRef.current > 0
          ? BASE_INTERVAL
          : getBaseInterval(wsStatusRef.current);
    } catch (error) {
      // Log error for debugging
      console.error('[CRM Polling] Failed', {
        orgId,
        error,
        nextInterval: Math.min(intervalRef.current * 2, MAX_INTERVAL),
      });
      // Exponential backoff on error
      intervalRef.current = Math.min(intervalRef.current * 2, MAX_INTERVAL);
    } finally {
      isPollingRef.current = false;
    }
  }, [orgId, queryClient, wsLastEventIdRef, forceReconnect]);

  const scheduleNextPoll = useCallback(() => {
    // Don't schedule if polling has been stopped (component unmounted)
    if (!activeRef.current) {
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      poll().then(scheduleNextPoll);
    }, withJitter(intervalRef.current));
  }, [poll]);

  useEffect(() => {
    // Skip if no queryClient (no QueryClientProvider), disabled, or no org
    if (!queryClient || !enabled || !orgId) {
      return;
    }

    // Mark polling as active
    activeRef.current = true;

    // Initial poll
    poll().then(scheduleNextPoll);

    // Visibility change handler — pause when hidden, resume with an immediate
    // poll when visible again.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause polling
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } else {
        // Resume with immediate poll (guard prevents concurrent polls)
        poll().then(scheduleNextPoll);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Stop polling before cleanup
      activeRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [queryClient, enabled, orgId, poll, scheduleNextPoll]);

  // Reset on org change, or whenever polling is disabled/re-enabled.
  //
  // `enabled` flips independently of `orgId` — OrgContext passes
  // `enabled: !!currentOrg?.id && !isBillingGated`, so a billing-gate change
  // toggles it while the org stays the same. Without `enabled` in the deps the
  // zombie-WS tracking state (stagnation counter and previous WS id/status)
  // survives a disable→enable cycle and is compared against a stale baseline,
  // making the escalation fire spuriously or not at all.
  useEffect(() => {
    lastEventIdRef.current = null;
    intervalRef.current = BASE_INTERVAL;
    stagnantPollCountRef.current = 0;
    prevWsEventIdRef.current = null;
    prevWsStatusRef.current = 'disconnected';
  }, [orgId, enabled]);
}
